import { execOnLoad, formatPrice } from '@general/helpers';
import { debounce, throttle } from 'lodash';
import './filters.scss';
import '../filters-active/filters-active.scss';
import { initPagination } from '@services/pagination';
import { closeModal } from '@components/modals';

if (!window.dAgency) window.dAgency = {};
window.dAgency.initFilters = (context) => {
  const snippetContainer = context.classList.contains('filters__container')
    ? context
    : context.closest('.filters__container');
  if (!snippetContainer) return;

  const form = snippetContainer.querySelector('.filters__form');
  if (!form) return;
  const modal = snippetContainer.closest('.modal');
  const filterContentSelector = '.filters__content';
  const activeFiltersContainerSelector = '#filters__active';
  const itemsCounterSelector = '[data-items-counter]';
  const activeFiltersCounterSelector = '.active__filters__count';
  const section = snippetContainer.closest('.shopify-section');
  const resultsWrapper = section.querySelector(filterContentSelector);
  const itemCounters = section.querySelectorAll(itemsCounterSelector);

  if (!resultsWrapper) {
    console.error(`Cannot find ${filterContentSelector} element. Cannot initialize filters`);
    return;
  }
  const urlCache = {};

  const getPageContent = async (url) => {
    if (!url) return;
    if (!urlCache[url]) {
      const page = await fetch(url).then((res) => res.text());
      urlCache[url] = page;
    }
    return new DOMParser().parseFromString(urlCache[url], 'text/html');
  };

  const updatePage = async () => {
    const searchParams = new URLSearchParams(new FormData(form));
    // Manually remove price range if not set by user
    const priceRange = form.querySelector('.filters__price');
    if (priceRange) {
      const rangeMin = priceRange.querySelector('input[type="range"][range-min]');
      const rangeMax = priceRange.querySelector('input[type="range"][range-max]');
      if (rangeMin && rangeMax) {
        if (rangeMin.value === rangeMin.min && rangeMax.value === rangeMax.max) {
          searchParams.delete(rangeMin.name);
          searchParams.delete(rangeMax.name);
        }
      }
    }

    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    const pageContent = await getPageContent(newUrl);
    const newForm = pageContent.querySelector(`#${form.id}`);
    form.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
      const newInput = newForm.querySelector(
        `input[type="${input.type}"][name="${input.name}"][value="${input.value}"]`,
      );
      if (!newInput || input.checked) return;
      input.disabled = newInput.disabled;
    });

    const newContent = newForm.closest('.shopify-section').querySelector(filterContentSelector);

    const newFilters = newForm.closest('.shopify-section').querySelector(activeFiltersContainerSelector);

    if (newFilters) {
      document.querySelector(activeFiltersContainerSelector).innerHTML = newFilters.innerHTML;
    }

    resultsWrapper.innerHTML = newContent.innerHTML;
    history.replaceState(null, '', newUrl);
    if (typeof window?.dAgency?.initProductTile === 'function')
      resultsWrapper
        .querySelectorAll('.product-tile__container')
        .forEach((tile) => window.dAgency.initProductTile(tile));
    initPagination({
      callbacks: {
        afterFetch: ({ components: { contentWrapperEl } }) => {
          if (typeof window?.dAgency?.initProductTile === 'function')
            contentWrapperEl
              .querySelectorAll('.product-tile__container')
              .forEach((tile) => window.dAgency.initProductTile(tile));
        },
      },
    });
    const newCount = newForm.closest('.shopify-section').querySelector(itemsCounterSelector);
    if (newCount) itemCounters.forEach((itemCounter) => (itemCounter.innerHTML = newCount.innerHTML));

    const activeFiltersCount = document.querySelector(activeFiltersCounterSelector);
    if (activeFiltersCount) {
      const count = newFilters
        ? newFilters.querySelectorAll('li').length
        : document.querySelector(activeFiltersContainerSelector).querySelectorAll('li').length;
      activeFiltersCount.innerText = count > 0 ? `(${count})` : '';
    }
  };

  const loadingTime = 1000;
  const changeTime = 500;
  const debounceLoading = throttle(() => {
    resultsWrapper.classList.toggle('loading');
    section.classList.toggle('loading');
  }, loadingTime);

  const debounceChange = debounce(async () => {
    debounceLoading();
    await updatePage();
    debounceLoading();
  }, changeTime);

  const applyFilters = modal.querySelector('.filters__btn[name="apply"]');
  if (applyFilters) {
    applyFilters.addEventListener('click', async (e) => {
      e.preventDefault();
      debounceChange();
      if (modal) closeModal(modal);
    });
  }

  const clearFilters = modal.querySelector('.filters__btn[name="clear"]');
  if (clearFilters) {
    clearFilters.addEventListener('click', async (e) => {
      e.preventDefault();
      form.reset();
      // reset all inputs
      form.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
        input.checked = false;
        input.disabled = false;
      });
      debounceChange();
      if (modal) closeModal(modal);
    });
  }

  form.addEventListener('change', async (e) => {
    e.preventDefault();
    if (/-Search$/.test(e.target.id)) return;
    debounceChange();
  });

  const sortingWrapper = form.querySelector('.filters__sorting');
  if (sortingWrapper) {
    const select = sortingWrapper.querySelector('select');
    const text = sortingWrapper.querySelector('span');

    if (select && text)
      select.addEventListener('change', (e) => (text.innerText = e.target.options[e.target.selectedIndex].text));
  }

  const priceRange = form.querySelector('.filters__price');
  if (priceRange) {
    const rangeMin = priceRange.querySelector('input[type="range"][range-min]');
    const rangeMax = priceRange.querySelector('input[type="range"][range-max]');
    const minValueLabel = priceRange.querySelectorAll('.filters__price-min, .filters__price-value-min');
    const maxValueLabel = priceRange.querySelectorAll('.filters__price-max, .filters__price-value-max');

    rangeMin.addEventListener('input', () =>
      minValueLabel.forEach(
        (label) =>
          (label.innerText = formatPrice({
            format: window?.dAgency?.strings?.moneyFormat?.replace(
              /{{.*?}}/,
              '{{amount_no_decimals_with_comma_separator}}',
            ),
            value: parseInt(rangeMin.value) * 100,
          })),
      ),
    );

    rangeMax.addEventListener('input', () =>
      maxValueLabel.forEach(
        (label) =>
          (label.innerText = formatPrice({
            format: window?.dAgency?.strings?.moneyFormat?.replace(
              /{{.*?}}/,
              '{{amount_no_decimals_with_comma_separator}}',
            ),
            value: parseInt(rangeMax.value) * 100,
          })),
      ),
    );
  }

  form.querySelectorAll('.filters__accordion .filters__search input[type="text"]').forEach((search) => {
    const labelTexts = search.closest('.filters__accordion').querySelectorAll('label span:not(.filters__value-swatch)');

    search.addEventListener('input', () =>
      labelTexts.forEach((text) => {
        const label = text.closest('label');
        label.style.display =
          !search.value || text.innerText.toLowerCase().indexOf(search.value.toLowerCase()) >= 0 ? '' : 'none';
      }),
    );
  });
};

execOnLoad(window.dAgency.initFilters, '.filters__container');
